import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Card,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Avatar,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyIcon from "@mui/icons-material/VpnKey";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../../auth/AuthProvider";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { tokens } from "../../theme";
import { pageContainerSx } from "../../components/ui";
// Email/phone shape checks (and their linear-time regexes) now live in the
// shared validators module, so this page and Register agree on what's valid.
import { validateForm, RULES } from "../../utils/validators";
import { PROFILE_SAVE_ERROR_MESSAGE } from "../../utils/errorMessages";

const SectionCard = ({ title, badge, right, children }) => (
  <Card sx={{ mb: 1.75, overflow: "hidden" }}>
    {title && (
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ p: "13px 16px", borderBottom: `1px solid ${tokens.border}` }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>{title}</Typography>
        {badge}
        {right && <Box sx={{ ml: "auto" }}>{right}</Box>}
      </Stack>
    )}
    {children}
  </Card>
);

SectionCard.propTypes = {
  title: PropTypes.node,
  badge: PropTypes.node,
  right: PropTypes.node,
  children: PropTypes.node,
};

// Shared field cell — same layout in read and edit modes, so toggling Edit
// never reflows the grid. Module scope (not inside Profile) keeps its component
// identity stable; nesting it would remount the TextField on every keystroke
// and drop focus.
const Field = ({ fld, value, editing, error, onChange }) => {
  // `alwaysRead` = never editable (admin-managed company fields). It renders
  // the same bordered box as read mode so Company details lines up exactly
  // with Login & contact, and it never becomes an input on Edit.
  const showReadBox = !editing || fld.alwaysRead;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
      <Typography
        component="label"
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: tokens.text3,
        }}
      >
        {fld.label}
      </Typography>
      {showReadBox ? (
        // read (or admin-managed): bordered box, same size as the edit input,
        // so every field aligns identically. Flat fill = static/read.
        <Box
          sx={{
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            px: 1.5,
            borderRadius: "10px",
            border: `1px solid ${tokens.border}`,
            // Slight grey fill for all read boxes (profile + company details),
            // so they read as fields at rest.
            background: tokens.surface2,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              fontStyle: value ? "normal" : "italic",
              color: value ? tokens.text : tokens.text3,
              wordBreak: "break-word",
            }}
          >
            {value || fld.empty || "—"}
          </Typography>
        </Box>
      ) : (
        // edit: same box, now an active input with a greyer fill so it's clear
        // these are editable. Read-only (email) gets a lock adornment.
        <TextField
          size="small"
          fullWidth
          value={value || ""}
          onChange={onChange(fld.key)}
          disabled={fld.readOnly}
          placeholder={fld.empty}
          error={!!error}
          helperText={error || undefined}
          InputProps={
            fld.readOnly
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <LockOutlinedIcon sx={{ fontSize: 16, color: tokens.text3 }} />
                    </InputAdornment>
                  ),
                }
              : undefined
          }
          // Editable fields differentiate by a greyer fill (surface3) + a grey
          // border — same restrained approach in light and dark. Accent shows
          // only on focus (not persistently), so no blue borders at rest.
          sx={{
            // Greyer fill applied to the root AND kept on hover/focus so the
            // field never flashes back to white (MUI's default) when active.
            "& .MuiOutlinedInput-root": {
              height: 40,
              borderRadius: "10px",
              backgroundColor: tokens.editFill,
            },
            "& .MuiOutlinedInput-root.Mui-focused": { backgroundColor: tokens.editFill },
            "& .MuiOutlinedInput-root:hover": { backgroundColor: tokens.editFill },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.borderStrong },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.text3 },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.accent,
              borderWidth: "2px",
            },
            "& .Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border },
            "& .MuiOutlinedInput-input": {
              fontSize: 14,
              fontWeight: 600,
              color: tokens.text,
              padding: "0 12px",
            },
            "& .MuiOutlinedInput-input::placeholder": { color: tokens.text3, opacity: 1 },
          }}
        />
      )}
    </Box>
  );
};

// Same logo resolution as the header avatar (Header.jsx).
const resolveLogoSrc = (rawLogo) => {
  if (!rawLogo) return null;
  return rawLogo.startsWith("http") ? rawLogo : `${CONFIG.BASE_URL}${rawLogo}`;
};

// Edit / Save / Cancel — shared between the standalone page header and the
// embedded card header.
const ProfileActions = ({ editing, saving, onEdit, onCancel, onSave }) => {
  if (!editing) {
    return (
      <Button
        variant="contained"
        size="small"
        startIcon={<EditIcon sx={{ fontSize: "15px !important" }} />}
        onClick={onEdit}
      >
        Edit profile
      </Button>
    );
  }
  return (
    <Stack direction="row" gap={1}>
      <Button
        variant="outlined"
        size="small"
        onClick={onCancel}
        sx={{ borderColor: tokens.border, color: tokens.text }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={
          saving ? (
            <CircularProgress size={12} sx={{ color: "#fff" }} />
          ) : (
            <SaveIcon sx={{ fontSize: "15px !important" }} />
          )
        }
        onClick={onSave}
        disabled={saving}
      >
        {saving ? "Saving…" : "Save changes"}
      </Button>
    </Stack>
  );
};

ProfileActions.propTypes = {
  editing: PropTypes.bool,
  saving: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

Field.propTypes = {
  fld: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    empty: PropTypes.string,
    alwaysRead: PropTypes.bool,
    readOnly: PropTypes.bool,
    full: PropTypes.bool,
  }).isRequired,
  value: PropTypes.string,
  editing: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

// `embedded` renders this inside the Settings tab pane (no page chrome), with
// `onNavigateSection`/`onChangePassword` switching tabs in place rather than
// route-navigating away. Defaults keep the standalone /profile route identical.
const Profile = ({ embedded = false, onNavigateSection, onChangePassword }) => {
  const navigate = useNavigate();
  // In-page shortcut: switch Settings tab when embedded, else route-navigate.
  const goSection = (sectionKey, route) =>
    embedded && onNavigateSection ? onNavigateSection(sectionKey) : navigate(route);
  // Change password: open the Settings dialog directly when embedded (one click,
  // no scroll); on the standalone page fall back to /settings.
  const goChangePassword = () => {
    if (embedded && onChangePassword) onChangePassword();
    else navigate("/settings");
  };
  const { updateUser, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setForm(data);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => (p[field] ? { ...p, [field]: "" } : p));
  };

  // Inline validation before a save. Only the user-editable contact fields are
  // validated (username/phone/contact name); email is read-only and company
  // details are admin-managed, so they're never blocked here.
  const validate = () =>
    validateForm(
      {
        username: RULES.username,
        // Previously unvalidated, so "." or "test" were both accepted.
        contactName: { ...RULES.person, label: "Contact name", required: false },
        // Email and phone stay optional here (email is read-only in the UI), but
        // when present they must be well formed.
        email: { ...RULES.email, required: false },
        phone: { ...RULES.phone, required: false },
      },
      form
    );

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      setSnack({ open: true, msg: "Please fix the highlighted fields", severity: "error" });
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setProfile(data);
      setForm(data);
      setEditing(false);
      updateUser({ companyName: data.companyName });
      setSnack({ open: true, msg: "Profile updated", severity: "success" });
    } catch {
      setSnack({ open: true, msg: PROFILE_SAVE_ERROR_MESSAGE, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: tokens.accent }} />
      </Box>
    );
  }

  // Company logo shown next to the page title — same resolve logic as the
  // header avatar (Header.jsx) so a relative /uploads/... path and a full URL
  // both render, with initials as the fallback when no logo is set.
  const rawLogo = profile?.companyLogo || null;
  const logoSrc = resolveLogoSrc(rawLogo);

  // Edit / Save / Cancel — shared between the standalone page header and the
  // embedded card header. Wrapped in a Stack with a gap so Cancel and Save never
  // touch (the card `right` slot doesn't add spacing on its own).
  const actionButtons = (
    <ProfileActions
      editing={editing}
      saving={saving}
      onEdit={() => setEditing(true)}
      onCancel={() => {
        setEditing(false);
        setForm(profile);
        setErrors({});
      }}
      onSave={handleSave}
    />
  );

  // Contact/login fields (editable). Order matches the 2×2 demo layout.
  const contactFields = [
    { key: "contactName", label: "Contact name", empty: "Add your contact name" },
    { key: "phone", label: "Phone number", empty: "Add your phone number" },
    { key: "email", label: "Email address", empty: "Add your email address" },
  ];

  // Company detail fields — admin-managed, so `alwaysRead: true` keeps them as
  // static bordered boxes (aligned with Login & contact) and never editable.
  const companyFields = [
    { key: "companyName", label: "Company name", empty: "Not provided", alwaysRead: true },
    { key: "industry", label: "Industry", empty: "Not provided", alwaysRead: true },
    { key: "website", label: "Website", empty: "Not provided", alwaysRead: true },
    { key: "address", label: "Address", full: true, empty: "Not provided", alwaysRead: true },
    { key: "city", label: "City", empty: "Not provided", alwaysRead: true },
    { key: "state", label: "State", empty: "Not provided", alwaysRead: true },
    { key: "country", label: "Country", full: true, empty: "Not provided", alwaysRead: true },
  ];

  return (
    <Box sx={pageContainerSx({ embedded })}>
      {!embedded && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/settings")}
          size="small"
          sx={{
            mb: 1.5,
            color: tokens.text2,
            "&:hover": { color: tokens.text, background: tokens.surface2 },
          }}
        >
          Back to Settings
        </Button>
      )}
      {/* Standalone page header: company logo + title. The "Login & contact"
          card below carries its own Edit/Save/Cancel actions in both modes. */}
      {!embedded && (
        <Stack direction="row" alignItems="center" gap={1.75} flexWrap="wrap" sx={{ mb: 2 }}>
          {logoSrc ? (
            <Avatar src={logoSrc} sx={{ width: 48, height: 48, borderRadius: "12px" }} />
          ) : (
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                background: tokens.accent2,
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              {(profile?.companyName || "B").slice(0, 2).toUpperCase()}
            </Avatar>
          )}
          <Box>
            <Typography variant="h1">Company Profile</Typography>
            <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.4 }}>
              Manage your company information and contact details
            </Typography>
          </Box>
        </Stack>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: embedded
            ? "1fr"
            : { xs: "1fr", md: "minmax(0,1.4fr) minmax(260px,1fr)" },
          gap: 1.75,
          alignItems: "start",
        }}
      >
        <Box>
          {/* Login & contact — the ONLY editable card. The grid is identical in
              read and edit modes (see Field), so toggling Edit never jumps. */}
          <SectionCard title="Login & contact" right={actionButtons}>
            <Box
              sx={{
                p: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: "16px 18px",
              }}
            >
              {contactFields.map((f) => (
                <Box key={f.key}>
                  <Field
                    fld={f}
                    value={form[f.key]}
                    editing={editing}
                    error={errors[f.key]}
                    onChange={handleChange}
                  />
                </Box>
              ))}
            </Box>
          </SectionCard>

          {/* Company details — always read-only for the user; these are managed
              by the administrator, so there is no edit input even when editing. */}
          <SectionCard
            title="Company details"
            right={
              <Chip
                size="small"
                label="Managed by administrator"
                icon={
                  <LockOutlinedIcon
                    sx={{ fontSize: "13px !important", color: `${tokens.text3} !important` }}
                  />
                }
                sx={{
                  height: 22,
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: tokens.text3,
                  background: tokens.surface3,
                }}
              />
            }
          >
            {/* Same grid + Field renderer as Login & contact, so the two cards
                line up exactly. `alwaysRead` keeps these as static boxes. */}
            <Box
              sx={{
                p: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: "16px 18px",
              }}
            >
              {companyFields.map((f) => (
                <Box key={f.key} sx={{ gridColumn: f.full ? { sm: "1 / -1" } : "auto" }}>
                  <Field
                    fld={f}
                    value={form[f.key]}
                    editing={editing}
                    error={errors[f.key]}
                    onChange={handleChange}
                  />
                </Box>
              ))}
            </Box>
          </SectionCard>
        </Box>

        {/* Right column — hidden when embedded: the Company summary duplicates
            Identity/Company details, and 'Security & more' duplicates the
            Settings Security card + the Payment/Privacy nav tabs. */}
        {!embedded && (
          <Box>
            {/* Company summary */}
            <SectionCard title="Company">
              <Box>
                {[
                  ["Company", profile?.companyName],
                  ["Industry", profile?.industry],
                  ["Contact", profile?.contactName],
                  [
                    "Member since",
                    profile?.createdAt
                      ? new Date(profile.createdAt).toLocaleDateString("en-IN", {
                          month: "long",
                          year: "numeric",
                        })
                      : "—",
                  ],
                ].map(([label, value], i, arr) => (
                  <Stack
                    key={label}
                    direction="row"
                    gap={1.5}
                    sx={{
                      p: "11px 16px",
                      borderBottom: i < arr.length - 1 ? `1px solid ${tokens.border}` : "none",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: tokens.text3, width: 110, flexShrink: 0 }}
                    >
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: tokens.text }}>
                      {value || "—"}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </SectionCard>

            {/* Security & more */}
            <SectionCard title="Security & more">
              <Stack gap={1} sx={{ p: 1.5 }}>
                {[
                  { label: "Change password", icon: KeyIcon, onClick: goChangePassword },
                  {
                    label: "Payment methods",
                    icon: CreditCardIcon,
                    onClick: () => goSection("payment", "/payment"),
                  },
                  {
                    label: "Privacy",
                    icon: LockOutlinedIcon,
                    onClick: () => goSection("privacy", "/privacy"),
                  },
                ].map((item) => (
                  <Button
                    key={item.label}
                    onClick={item.onClick}
                    startIcon={<item.icon sx={{ fontSize: "16px !important" }} />}
                    sx={{
                      justifyContent: "flex-start",
                      color: tokens.text,
                      background: tokens.surface2,
                      border: `1px solid ${tokens.border}`,
                      "&:hover": { background: tokens.surface3 },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Button
                  onClick={() => {
                    logout();
                    navigate("/login", { replace: true });
                  }}
                  startIcon={<LogoutIcon sx={{ fontSize: "16px !important" }} />}
                  sx={{
                    justifyContent: "flex-start",
                    color: tokens.danger,
                    background: tokens.dangerSoft,
                    "&:hover": { background: tokens.dangerSoft },
                  }}
                >
                  Log out
                </Button>
              </Stack>
            </SectionCard>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} sx={{ borderRadius: "10px" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

Profile.propTypes = {
  embedded: PropTypes.bool,
  onNavigateSection: PropTypes.func,
  onChangePassword: PropTypes.func,
};

export default Profile;
